// covered-by: frontends/carrie/src/ask-v2/services/__tests__/reviews.test.ts
import {
  createAskPunttReviewCommentResponseSchema,
  getAskPunttReviewResponseSchema,
  updateAskPunttReviewCommentResponseSchema,
  type AskPunttReviewCommentWriteBody,
  type CreateAskPunttReviewCommentResponse,
  type GetAskPunttReviewResponse,
  type UpdateAskPunttReviewCommentResponse,
} from "@mg/schemas/src/christo/askPunttReview";
import { ticketMessageCitationSchema } from "@mg/schemas/src/commons/ticket";

import { API_URL } from "../../config/env";
import { smartFetch } from "../../utils/http";

const reviewsBase = `${API_URL}/private/catalyst/ask-puntt/reviews`;

/**
 * Repair a citation this client cannot read, and only drop what cannot be saved.
 *
 * `askPunttReviewCommentSchema.citations` is an array of a UNION, so one
 * unreadable entry fails its comment, which fails the review, which fails the
 * whole response — and `getAskPunttReview` throws, so a review of seven
 * findings renders as an error because one chip was malformed. A citation is
 * an ANNOTATION on a comment; losing it must cost the chip, not the finding a
 * reviewer has to act on.
 *
 * This is the same trade christo already makes on the chat path, where
 * `warholChat.ts` safeParse-drops each warhol knowledge citation before
 * storing it. The review path had no equivalent, and a rule citation whose
 * `location` was a sheet LABEL rather than the object this schema declares
 * blanked a live review on the warhol-review-kdb epic.
 *
 * Deliberately narrow: only `citations` entries are dropped. Anything else
 * malformed still throws, because a comment with no text or a review with no
 * id is corruption a silent recovery would hide.
 */
/**
 * The citation with its unreadable FIELDS removed, or null if nothing is left.
 *
 * Dropping the whole record for one bad field costs far more than it saves. The
 * live case on this branch: a review comment carried a rule citation whose
 * `location` was the source SHEET LABEL where the schema declares an object,
 * while its `ruleId`, `statement`, `quote` and `sourceName` were all fine. A
 * record thrown away for that takes the rule chip AND the inline claim link
 * with it -- so the reader loses the citation entirely to save them from a
 * missing highlight.
 *
 * Driven by zod's own issue paths rather than a list of fields this file
 * guesses at: whatever the schema rejected is what gets removed, so a future
 * field is handled without being enumerated here. Required fields cannot be
 * repaired this way -- removing one only moves the error -- so the retry is
 * capped and a record that still fails is dropped as before.
 */
function repairCitation(citation: unknown): unknown | null {
  if (ticketMessageCitationSchema.safeParse(citation).success) return citation;
  if (citation == null || typeof citation !== "object") return null;

  let candidate: Record<string, unknown> = {
    ...(citation as Record<string, unknown>),
  };
  // One pass per offending top-level key, bounded: each pass must strictly
  // shrink the object, so this cannot loop.
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const parsed = ticketMessageCitationSchema.safeParse(candidate);
    if (parsed.success) return candidate;
    const offending = deletableKeys(parsed.error, candidate);
    if (offending.size === 0) return null;
    const next: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(candidate)) {
      if (!offending.has(key)) next[key] = value;
    }
    if (Object.keys(next).length === Object.keys(candidate).length) return null;
    candidate = next;
  }
  return null;
}

/**
 * The keys to remove to get `candidate` past the schema, or none.
 *
 * `ticketMessageCitationSchema` is a UNION, and a failed union reports one
 * `invalid_union` issue at the root with the real errors nested in
 * `unionErrors` -- so reading `issue.path` alone finds nothing to remove and
 * every repairable record looks unrepairable.
 *
 * Members whose complaints are about ABSENT keys are skipped: a file citation
 * rejecting a rule citation for having no `url` is not a field to delete, it is
 * the wrong member. What is left is the member this record was nearly valid
 * for, and the fewest deletions that satisfy it.
 */
function deletableKeys(
  error: { issues: unknown[] },
  candidate: Record<string, unknown>,
): Set<string> {
  type Issue = { path?: unknown[]; unionErrors?: { issues: Issue[] }[] };
  const issues = error.issues as Issue[];

  const keysOf = (from: Issue[]): Set<string> => {
    const keys = new Set<string>();
    for (const issue of from) {
      const key = issue.path?.[0];
      if (typeof key === "string" && key in candidate) keys.add(key);
    }
    return keys;
  };

  const direct = keysOf(issues);
  if (direct.size > 0) return direct;

  const members = issues.flatMap((issue) => issue.unionErrors ?? []);
  let best: Set<string> | null = null;
  for (const member of members) {
    const keys = keysOf(member.issues);
    // Every complaint must be about a key we actually hold, or removing them
    // cannot help and we would be guessing at the wrong union member.
    if (keys.size === 0 || keys.size !== member.issues.length) continue;
    if (!best || keys.size < best.size) best = keys;
  }
  return best ?? new Set<string>();
}

function withReadableCitations(json: unknown): unknown {
  const root = json as { review?: { comments?: unknown } } | null;
  const comments = root?.review?.comments;
  if (!Array.isArray(comments)) return json;
  return {
    ...(root as object),
    review: {
      ...(root as { review: object }).review,
      comments: comments.map((comment) => {
        const citations = (comment as { citations?: unknown })?.citations;
        if (!Array.isArray(citations)) return comment;
        const readable: unknown[] = [];
        let changed = false;
        for (const citation of citations) {
          const repaired = repairCitation(citation);
          if (repaired === null) {
            changed = true;
            continue;
          }
          if (repaired !== citation) changed = true;
          readable.push(repaired);
        }
        return changed
          ? { ...(comment as object), citations: readable }
          : comment;
      }),
    },
  };
}

export async function getAskPunttReview(
  reviewId: string,
): Promise<GetAskPunttReviewResponse> {
  const json = await smartFetch(`${reviewsBase}/${reviewId}`);
  return getAskPunttReviewResponseSchema.parse(withReadableCitations(json));
}

/**
 * Adds a human comment to a V2 review. christo stamps `createdBy` from the
 * caller's token and stores the pin location (`rel_x`/`rel_y`/`page_num`) on
 * the review document.
 */
export async function createAskPunttReviewComment(
  reviewId: string,
  body: AskPunttReviewCommentWriteBody,
): Promise<CreateAskPunttReviewCommentResponse> {
  const json = await smartFetch(`${reviewsBase}/${reviewId}/comments`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return createAskPunttReviewCommentResponseSchema.parse(json);
}

export async function updateAskPunttReviewComment(
  reviewId: string,
  commentId: string,
  body: Partial<AskPunttReviewCommentWriteBody>,
): Promise<UpdateAskPunttReviewCommentResponse> {
  const json = await smartFetch(
    `${reviewsBase}/${reviewId}/comments/${commentId}`,
    {
      method: "PUT",
      body: JSON.stringify(body),
    },
  );
  return updateAskPunttReviewCommentResponseSchema.parse(json);
}
